import {memo} from 'react'
import { isNull, map } from "lodash";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import { t } from "i18next";

import { BadgeSwitch, ProgressiveImage, Icon } from "components";
import {
  selectVideos,
  getButtonBgColor,
  setIsVideoTourScreen,
  setFocusedVideoIdx,
  setJaiEditUrl,
  selectSiteId,
  selectPropertyId,
  getSelectedBadgeVideo,
  getJaiEditUrl,
  selectIsJAI,
  getBadgeSwitchVideos,
  setSelectedVideoSwitchId,
  getIsMaximized,
  getSelectedBadgeVideoId,
} from "slices";

import ExtraOption from "../ExtraOptions";

const VideosGalleryView = memo(({ showButtons = true }) => {
  const dispatch = useDispatch();

  const isMaximized = useSelector(getIsMaximized);
  const selectedBadgeItem = useSelector(getSelectedBadgeVideo);
  const galleries = useSelector(selectVideos);
  const color = useSelector(getButtonBgColor);
  const siteId = useSelector(selectSiteId);
  const propertyId = useSelector(selectPropertyId);
  const jaiEditURL = useSelector(getJaiEditUrl);
  const isJAI = useSelector(selectIsJAI);
  const badgeItems = useSelector(getBadgeSwitchVideos);
  const selectedBadgeId = useSelector(getSelectedBadgeVideoId);

  const [galleryItems, setGalleryItems] = useState([]);

  const onClickTourItem = (idx) => {
    dispatch(setFocusedVideoIdx(idx));
    dispatch(setIsVideoTourScreen(true));
  };

  const configureEditUrl = () => {
    const { id } = selectedBadgeItem;

    switch (id) {
      case "videos":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/tours/${propertyId}/sites/${siteId}/edit?experience=1`));
        break;
      case "3d_videos":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/tours/${propertyId}/sites/${siteId}/edit?experience=1#3d_video`));

        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isNull(galleries) && !isNull(selectedBadgeItem)) {
      setGalleryItems(map(galleries[selectedBadgeItem.id]));
      configureEditUrl();
    }
  }, [galleries, selectedBadgeItem]);

  useEffect(() => {
    return () => {
      setGalleryItems([]);
    };
  }, []);

  const dataArr = isMaximized ? galleryItems?.slice(0, 10) : galleryItems?.slice(0, 4);

  const maxLength = isMaximized ? 10 : 4;

  const buttons =
    galleryItems?.length > maxLength
      ? [
          {
            text: t("seeAllVideos", { count: galleryItems.length }),
            handleClick: () => onClickTourItem(0),
          },
        ]
      : null;

  const extraButtons = isJAI
    ? [
        {
          text: t("editSubOrg"),
          handleClick: () => window.open(jaiEditURL, "_blank"),
        },
      ]
    : null;

  return (
    <>
      <div className="hayley-h-fit hayley-w-full">
        <BadgeSwitch
          module="videos"
          disabled={false}
          badgeItems={badgeItems}
          setSelectedItem={(id) => dispatch(setSelectedVideoSwitchId(id))}
          selectedItem={selectedBadgeItem}
        />

        <div>
          <div className={`hayley-grid hayley-w-full ${isMaximized ? "hayley-grid-cols-5" : "hayley-grid-cols-2"} hayley-gap-2`}>
            {dataArr?.map?.((d, idx) => (
              <Box data-testid="video_container" key={idx} className="hayley-space-y-1">
                <Box className="hayley-relative">
                  <ProgressiveImage
                    classes={{ root: `hayley-h-[130px] hayley-overflow-hidden` }}
                    src={d.thumbnail}
                    alt={d.title}
                    selectedId={selectedBadgeId}
                  />

                  <Box
                    className="hayley-custom hayley-absolute hayley-left-0 hayley-top-0 hayley-flex hayley-h-full hayley-w-full hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-border hayley-border-solid hayley-border-transparent"
                    sx={{ "&.hayley-custom:hover": { borderColor: `${color} !important` } }}
                    onClick={() => onClickTourItem(idx)}
                  >
                    <Box className="hayley-flex hayley-h-8 hayley-w-8 hayley-items-center hayley-justify-center hayley-rounded-full hayley-bg-white/80">
                      <Icon
                        iconType={selectedBadgeItem.id === "pre_recorded_video" ? "VideoPlay" : "3DTourPlay"}
                        className="hayley-h-full hayley-w-full hayley-text-secondary"
                      />
                    </Box>
                  </Box>
                </Box>
                <div data-testid="video_title" className="hayley-text-xs hayley-text-secondary">
                  {d.title}
                </div>
              </Box>
            ))}
          </div>
        </div>
        {showButtons ? <ExtraOption from="videos" buttons={buttons} extraButtons={extraButtons} /> : null}
      </div>
    </>
  );
});

export default VideosGalleryView;
